// src/components/IncomingOrders/OrderDetailsDrawer.jsx

import { useCallback, useEffect, useRef, useState } from "react";
import { useMediaQuery, Box, SwipeableDrawer } from "@mui/material";
import OrderInspector from "./OrderInspector";

const INITIAL_WIDTH = 480;
const MIN_WIDTH = 320;
const MAX_WIDTH = parseInt(0.9 * window.innerWidth);
const STORAGE_KEY = "orderDrawerWidth";

const OrderDetailsDrawer = ({
  open,
  order,
  orderError,
  orderLoading,
  orderUpdating,
  orderUpdateError,
  onClose,
  onUpdateOrder,
  initialWidth = INITIAL_WIDTH,
  onOpenStatusManager
}) => {
  const isMobile = useMediaQuery("(max-width: 900px)");

  const maxWidth = isMobile
    ? "100vw"
    : "90vw";

  // ⭐ Load saved width (desktop only)
  const savedWidth = !isMobile
    ? parseInt(localStorage.getItem(STORAGE_KEY), 10)
    : null;

  const [width, setWidth] = useState(
    savedWidth && savedWidth >= MIN_WIDTH
      ? savedWidth
      : initialWidth
  );

  const [isAnimating, setAnimating] = useState(false);

  const isDragging = useRef(false);

  const handleMouseDown = (e) => {
    if (isMobile) return;
    isDragging.current = true;
    setAnimating(false);
    e.preventDefault();
  };

  const handleMouseMove = useCallback((e) => {
    if (!isDragging.current) return;

    const newWidth = Math.min(
      Math.max(MIN_WIDTH, window.innerWidth - e.clientX),
      MAX_WIDTH
    );

    setWidth(newWidth);
    localStorage.setItem(STORAGE_KEY, String(newWidth));
  }, []);

  const handleMouseUp = () => {
    if (isDragging.current) {
      setAnimating(true);
    }
    isDragging.current = false;
  };

  useEffect(() => {
    if (isMobile) return;

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [handleMouseMove, isMobile]);

  // ⭐ AUTO-COLLAPSE ON MOBILE OUTSIDE TAP
  //useEffect(() => {
  //  if (!isMobile || !open) return;
  //
  //  const handleTap = (e) => {
  //    const drawerPaper = document.querySelector(".MuiDrawer-paper");
  //    if (drawerPaper && !drawerPaper.contains(e.target)) {
  //      onClose();
  //    }
  //  };
  //
  //  document.addEventListener("mousedown", handleTap);
  //  document.addEventListener("touchstart", handleTap);
  //
  //  return () => {
  //    document.removeEventListener("mousedown", handleTap);
  //    document.removeEventListener("touchstart", handleTap);
  //  };
  //}, [open, isMobile, onClose]);

  return (
    <SwipeableDrawer
      anchor="right"
      open={open}
      disableSwipeToOpen={false}
      onClose={onClose}  // "Close on Escape"
      onOpen={() => { }}
      ModalProps={{
        disableAutoFocus: true,
        disableEnforceFocus: true,
        disableRestoreFocus: true,
        hideBackdrop: true,
        sx: {
          left: "unset",
        },
      }}
      PaperProps={{
        sx: {
          width: isMobile ? "100vw" : width,
          maxWidth,
          overflow: "hidden",
          position: "relative",
          display: "flex",
          flexDirection: "column",
          // ⭐ Smooth animation when NOT dragging
          transition: isDragging.current
            ? "none"
            : isAnimating
              ? "width 0.25s ease"
              : "none",
        },
      }}
    >
      {/* ⭐ Drag handle — with visual grip icon (••••) */}
      {!isMobile && (
        <Box
          onMouseDown={handleMouseDown}
          sx={{
            position: "absolute",
            left: 0,
            top: 0,
            bottom: 0,
            width: "10px",
            cursor: "col-resize",
            zIndex: 2000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            "&:hover": {
              backgroundColor: "rgba(0,0,0,0.06)",
            },
          }}
        >
          <Box
            sx={{
              writingMode: "vertical-rl",
              transform: "rotate(180deg)",
              fontSize: "10px",
              letterSpacing: "3px",
              color: "#888",
              userSelect: "none",
            }}
          >
            ••••
          </Box>
        </Box>
      )}

      {/* ⭐ Content */}
      <Box sx={{ flex: 1, overflowY: "auto" }}>
        <OrderInspector
          order={order}
          orderError={orderError}
          orderLoading={orderLoading}
          orderUpdating={orderUpdating}
          orderUpdateError={orderUpdateError}
          width={width}
          onClose={onClose}
          onUpdateOrder={onUpdateOrder}
          onOpenStatusManager={onOpenStatusManager}
          // onWidthChange MUST NOT BE USED FROM OrderDetailsDrawer
        />
      </Box>
    </SwipeableDrawer>
  );
}

OrderDetailsDrawer.displayName = "OrderDetailsDrawer";

export default OrderDetailsDrawer;
